import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  varchar,
  index,
  uniqueIndex,
  jsonb,
  decimal,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { users } from "./auth";
import { appointments } from "./support";
import { clients, prospects } from "./business";

// Calendar account connections (OAuth tokens)
export const calendarAccounts = pgTable(
  "calendar_accounts",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    provider: varchar("provider", { length: 20 }).notNull(), // "google" | "outlook"
    email: varchar("email", { length: 255 }).notNull(),
    calendarId: text("calendar_id"), // Primary calendar ID (e.g., "primary" for Google)
    accessToken: text("access_token").notNull(),
    refreshToken: text("refresh_token"),
    tokenExpiresAt: timestamp("token_expires_at"),
    syncCursor: text("sync_cursor"), // Google syncToken / Outlook deltaLink
    lastSyncedAt: timestamp("last_synced_at"),
    isActive: boolean("is_active").notNull().default(true),
    syncDirection: varchar("sync_direction", { length: 20 }).default("both"), // "inbound" | "outbound" | "both"
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    userIdIdx: index("idx_calendar_accounts_user_id").on(table.userId),
    emailIdx: index("idx_calendar_accounts_email").on(table.email),
    userProviderUnique: uniqueIndex("uq_calendar_accounts_user_provider_email").on(
      table.userId,
      table.provider,
      table.email
    ),
  })
);

// Synced calendar events storage
export const syncedCalendarEvents = pgTable(
  "synced_calendar_events",
  {
    id: serial("id").primaryKey(),
    calendarAccountId: integer("calendar_account_id")
      .notNull()
      .references(() => calendarAccounts.id, { onDelete: "cascade" }),
    providerEventId: text("provider_event_id").notNull(),
    recurringEventId: text("recurring_event_id"), // For recurring event instances
    title: text("title"),
    description: text("description"),
    location: text("location"),
    startTime: timestamp("start_time").notNull(),
    endTime: timestamp("end_time").notNull(),
    isAllDay: boolean("is_all_day").default(false),
    status: varchar("status", { length: 50 }), // confirmed, tentative, cancelled
    visibility: varchar("visibility", { length: 50 }), // public, private, confidential
    organizer: jsonb("organizer"), // { email, displayName, self }
    attendees: jsonb("attendees"), // [{ email, displayName, responseStatus, optional }]
    reminders: jsonb("reminders"), // { useDefault, overrides: [{ method, minutes }] }
    conferenceData: jsonb("conference_data"), // Video call info (Google Meet, Teams, etc.)
    htmlLink: text("html_link"), // Link to view event in provider's calendar
    iCalUID: text("ical_uid"),
    etag: text("etag"), // For change detection
    linkedAppointmentId: integer("linked_appointment_id").references(
      () => appointments.id,
      { onDelete: "set null" }
    ),
    syncDirection: varchar("sync_direction", { length: 20 }).default("inbound"), // "inbound" | "outbound"
    syncedAt: timestamp("synced_at").defaultNow(),
  },
  (table) => ({
    calendarAccountIdIdx: index("idx_synced_events_account_id").on(
      table.calendarAccountId
    ),
    providerEventIdx: index("idx_synced_events_provider_id").on(
      table.calendarAccountId,
      table.providerEventId
    ),
    startTimeIdx: index("idx_synced_events_start_time").on(table.startTime),
    endTimeIdx: index("idx_synced_events_end_time").on(table.endTime),
    linkedAppointmentIdx: index("idx_synced_events_linked_appointment").on(
      table.linkedAppointmentId
    ),
  })
);

// Calendar AI analysis results
export const calendarAiAnalysis = pgTable(
  "calendar_ai_analysis",
  {
    id: serial("id").primaryKey(),
    calendarAccountId: integer("calendar_account_id")
      .notNull()
      .references(() => calendarAccounts.id, { onDelete: "cascade" }),
    analyzedAt: timestamp("analyzed_at").notNull().defaultNow(),
    analysisType: varchar("analysis_type", { length: 50 }).notNull(), // conflicts, optimal_times, follow_ups, busy_patterns
    findings: jsonb("findings"), // Detailed findings from analysis
    suggestions: jsonb("suggestions"), // Array of suggested actions
    periodStart: timestamp("period_start"),
    periodEnd: timestamp("period_end"),
    status: varchar("status", { length: 20 }).default("pending"), // pending, reviewed, dismissed
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    accountIdIdx: index("idx_calendar_analysis_account_id").on(
      table.calendarAccountId
    ),
    typeIdx: index("idx_calendar_analysis_type").on(table.analysisType),
    statusIdx: index("idx_calendar_analysis_status").on(table.status),
  })
);

// Calendar AI suggestions (actionable items)
export const calendarAiSuggestions = pgTable(
  "calendar_ai_suggestions",
  {
    id: serial("id").primaryKey(),
    calendarAccountId: integer("calendar_account_id")
      .notNull()
      .references(() => calendarAccounts.id, { onDelete: "cascade" }),
    syncedEventId: integer("synced_event_id").references(
      () => syncedCalendarEvents.id,
      { onDelete: "set null" }
    ),
    suggestionType: varchar("suggestion_type", { length: 50 }).notNull(), // follow_up, optimal_time, reschedule, conflict
    title: text("title").notNull(),
    description: text("description"),
    suggestedStart: timestamp("suggested_start"),
    suggestedEnd: timestamp("suggested_end"),
    relatedClientId: integer("related_client_id").references(() => clients.id, {
      onDelete: "set null",
    }),
    relatedProspectId: integer("related_prospect_id").references(
      () => prospects.id,
      { onDelete: "set null" }
    ),
    confidence: decimal("confidence", { precision: 3, scale: 2 }), // 0.00 - 1.00
    reasoning: text("reasoning"),
    status: varchar("status", { length: 20 }).default("pending"), // pending, accepted, rejected, dismissed
    createdAppointmentId: integer("created_appointment_id").references(
      () => appointments.id,
      { onDelete: "set null" }
    ),
    acceptedAt: timestamp("accepted_at"),
    rejectedAt: timestamp("rejected_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    accountIdIdx: index("idx_calendar_suggestions_account_id").on(
      table.calendarAccountId
    ),
    typeIdx: index("idx_calendar_suggestions_type").on(table.suggestionType),
    statusIdx: index("idx_calendar_suggestions_status").on(table.status),
    eventIdIdx: index("idx_calendar_suggestions_event_id").on(table.syncedEventId),
  })
);

// Insert schemas
export const insertCalendarAccountSchema = createInsertSchema(calendarAccounts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertSyncedCalendarEventSchema = createInsertSchema(
  syncedCalendarEvents
).omit({
  id: true,
  syncedAt: true,
});

export const insertCalendarAiAnalysisSchema = createInsertSchema(
  calendarAiAnalysis
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCalendarAiSuggestionSchema = createInsertSchema(
  calendarAiSuggestions
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type CalendarAccount = typeof calendarAccounts.$inferSelect;
export type InsertCalendarAccount = z.infer<typeof insertCalendarAccountSchema>;
export type SyncedCalendarEvent = typeof syncedCalendarEvents.$inferSelect;
export type InsertSyncedCalendarEvent = z.infer<typeof insertSyncedCalendarEventSchema>;
export type CalendarAiAnalysis = typeof calendarAiAnalysis.$inferSelect;
export type InsertCalendarAiAnalysis = z.infer<typeof insertCalendarAiAnalysisSchema>;
export type CalendarAiSuggestion = typeof calendarAiSuggestions.$inferSelect;
export type InsertCalendarAiSuggestion = z.infer<typeof insertCalendarAiSuggestionSchema>;

// Provider type enum for type safety
export type CalendarProvider = "google" | "outlook";

// Sync direction enum
export type CalendarSyncDirection = "inbound" | "outbound" | "both";

// Suggestion type enum
export type CalendarSuggestionType = "follow_up" | "optimal_time" | "reschedule" | "conflict";
